import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "../hooks/useAuth";
import useChat from "../hooks/useChat";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import playNotificationSound from "../layouts/components/Notifications/notificationSound";
import showBrowserNotification from "../pages/Chat/utils/browserNotification";
import { notificationMessage } from "../layouts/components/Notifications/notificationMessage";

const NotificationContext = createContext({});

// Rides on the socket ChatProvider already opened rather than dialling a second one - two
// socket.io connections per tab would double the server's presence bookkeeping for no gain.
// This provider must therefore render inside ChatProvider.
export const NotificationProvider = ({ children }) => {
    const { t } = useTranslation();
    const { auth } = useAuth();
    const { socket } = useChat();
    const axiosPrivate = useAxiosPrivate();
    const axiosRef = useRef(axiosPrivate);
    axiosRef.current = axiosPrivate;

    const [unreadCount, setUnreadCount] = useState(0);
    const [items, setItems] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);

    const refreshUnreadCount = useCallback(async () => {
        try {
            const res = await axiosRef.current.get('/notifications/unread-count');
            setUnreadCount(res.data?.data?.total || 0);
        } catch (error) { /* silent - the badge just stays as-is until the next event */ }
    }, []);

    // beforeId pages backwards from the oldest row already held; omitting it reloads from the top.
    const loadPage = useCallback(async ({ beforeId = null, module = null } = {}) => {
        setLoading(true);
        try {
            const params = { limit: 20 };
            if (beforeId) params.beforeId = beforeId;
            if (module) params.module = module;

            const res = await axiosRef.current.get('/notifications', { params });
            const page = res.data?.data || [];

            setItems(prev => beforeId ? [...prev, ...page] : page);
            setHasMore(!!res.data?.hasMore);
            return page;
        } catch (error) {
            if (!beforeId) setItems([]);
            return [];
        } finally { setLoading(false) }
    }, []);

    // Opening the panel clears the badge without claiming each row was read - unread highlighting
    // survives until the user actually opens one.
    const markSeen = useCallback(async () => {
        setUnreadCount(0);
        try { await axiosRef.current.post('/notifications/seen'); }
        catch (error) { refreshUnreadCount() }
    }, [refreshUnreadCount]);

    const markRead = useCallback(async (ids) => {
        const list = Array.isArray(ids) ? ids : [ids];
        setItems(prev => prev.map(item => list.includes(item.id) ? { ...item, readAt: new Date().toISOString() } : item));
        try {
            const res = await axiosRef.current.post('/notifications/read', { ids: list });
            setUnreadCount(res.data?.data?.total || 0);
        } catch (error) { refreshUnreadCount() }
    }, [refreshUnreadCount]);

    const markAllRead = useCallback(async () => {
        const now = new Date().toISOString();
        setItems(prev => prev.map(item => item.readAt ? item : { ...item, readAt: now }));
        setUnreadCount(0);
        try { await axiosRef.current.post('/notifications/read', { all: true }); }
        catch (error) { refreshUnreadCount() }
    }, [refreshUnreadCount]);

    const archive = useCallback(async (id) => {
        setItems(prev => prev.filter(item => item.id !== id));
        try {
            const res = await axiosRef.current.delete(`/notifications/archive/${id}`);
            setUnreadCount(res.data?.data?.total || 0);
        } catch (error) { refreshUnreadCount() }
    }, [refreshUnreadCount]);

    // Re-checks module and entity permissions server-side before handing back a target, since
    // the notification's stored params were snapshotted outside those checks.
    const open = useCallback(async (id) => {
        try {
            const res = await axiosRef.current.post(`/notifications/open/${id}`);
            setItems(prev => prev.map(item => item.id === id ? { ...item, readAt: new Date().toISOString() } : item));
            refreshUnreadCount();
            return res.data?.data || null;
        } catch (error) { return null }
    }, [refreshUnreadCount]);

    useEffect(() => {
        if (!socket) return;

        const onNew = ({ data }) => {
            setItems(prev => prev.some(item => item.id === data.id) ? prev : [data, ...prev]);
            setUnreadCount(prev => prev + 1);

            // Chimes for every notification, the way chat does for every message - the server
            // has already dropped anything the user muted, so whatever reaches here is wanted.
            // CRITICAL gets the insistent variant of the same motif.
            playNotificationSound(data.level);
            // Its own tag, so an approval popup and an incoming chat message don't replace each other
            showBrowserNotification(t('NOTIFICATIONS'), notificationMessage(data, t), 'notification');
        };

        // Emitted after a read/archive anywhere, so a second tab's badge follows along.
        const onCount = ({ data }) => setUnreadCount(data?.total || 0);

        socket.on('notification:new', onNew);
        socket.on('notification:count', onCount);

        return () => {
            socket.off('notification:new', onNew);
            socket.off('notification:count', onCount);
        };
    }, [socket, t]);

    useEffect(() => {
        if (!auth?.token || auth.token === 'Unauthorized') {
            setUnreadCount(0);
            setItems([]);
            return;
        }
        refreshUnreadCount();
    }, [auth?.token, refreshUnreadCount]);

    return (
        <NotificationContext.Provider value={{
            unreadCount, items, hasMore, loading,
            loadPage, markSeen, markRead, markAllRead, archive, open, refreshUnreadCount,
        }}>
            {children}
        </NotificationContext.Provider>
    );
}

export default NotificationContext;
